import { FC } from 'react';
import styled, { CSSObject } from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { Box } from '../box';

export interface LetterIconProps {
  letter: string;
  color?: keyof typeof globalTheme.colors;
  backgroundColor?: keyof typeof globalTheme.colors;
  size?: keyof typeof globalTheme.space;
  fontWeight?: keyof typeof globalTheme.fontWeights;
  fontSize?: keyof typeof globalTheme.fontSizes;
  className?: string;
  lettersCount?: 1 | 2;
  hasBorder?: boolean;
}

function getSize(props: Pick<LetterIconProps, 'size'>): CSSObject {
  const sizeValue =
    props.size !== undefined ? globalTheme.space[props.size] : '1em';
  return {
    width: sizeValue,
    height: sizeValue,
  };
}

const Wrapper = styled(Box)<{
  $hasBorder?: boolean;
  size?: LetterIconProps['size'];
}>`
  ${getSize};
  position: relative;
  border-radius: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: ${(props) =>
    props.$hasBorder ? `1px solid ${globalTheme.colors.lightGray}` : 'none'};
`;

const Icon = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-weight: ${globalTheme.fontWeights.light};
  text-transform: uppercase;
  fill: currentColor;
`;

function getInitials(input: string, count: 1 | 2): string {
  const trimmed = input.trim();
  if (!trimmed) return '';

  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);

  if (count === 1) {
    return words[0][0].toUpperCase();
  } else {
    if (words.length === 1) {
      return trimmed.slice(0, 2).toUpperCase();
    } else {
      const firstInitial = words[0][0];
      const secondInitial = words[1][0];
      return (firstInitial + secondInitial).toUpperCase();
    }
  }
}

export const LetterIcon: FC<LetterIconProps> = ({
  className,
  letter,
  color,
  backgroundColor,
  fontWeight: fontWeightProp,
  size,
  fontSize: fontSizeProp,
  lettersCount = 1,
  hasBorder,
}) => {
  const displayLetters = getInitials(letter, lettersCount);

  const sizeValue = (size && globalTheme.space[size]) || '32';
  const viewBoxSize = parseInt(sizeValue.toString(), 10);
  const fontSize = fontSizeProp
    ? globalTheme.fontSizes[fontSizeProp]
    : viewBoxSize / (lettersCount === 2 ? 2.8 : 2);
  const fontWeight = fontWeightProp
    ? globalTheme.fontWeights[fontWeightProp]
    : globalTheme.fontWeights.regular;

  return (
    <Wrapper
      size={size}
      color={color}
      backgroundColor={backgroundColor}
      aria-hidden
      className={className}
      $hasBorder={hasBorder}
    >
      <Icon viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}>
        <text
          textAnchor='middle'
          dominantBaseline='central'
          x='50%'
          y='50%'
          fontSize={fontSize}
          fontWeight={fontWeight}
        >
          {displayLetters}
        </text>
      </Icon>
    </Wrapper>
  );
};
